import { createContext, useContext, useState, useEffect } from 'react';
import axios from 'axios';

const AuthContext = createContext(null);

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(localStorage.getItem('token'));
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const initAuth = async () => {
      const storedToken = localStorage.getItem('token');
      if (storedToken) {
        try {
          const response = await axios.get(`${API_URL}/auth/me`, {
            headers: { Authorization: `Bearer ${storedToken}` }
          });
          setUser(response.data);
          setToken(storedToken);
        } catch (error) {
          localStorage.removeItem('token');
          setToken(null);
          setUser(null);
        }
      }
      setIsLoading(false);
    };
    initAuth();
  }, []);

  const login = async (email, password) => {
    const response = await axios.post(`${API_URL}/auth/login`, { email, password });
    const { access_token, user: userData } = response.data;
    localStorage.setItem('token', access_token);
    setToken(access_token);
    setUser(userData);
    return userData;
  };

  const register = async (userData) => {
    const response = await axios.post(`${API_URL}/auth/register`, userData);
    const { access_token, user: newUser } = response.data;
    localStorage.setItem('token', access_token);
    setToken(access_token);
    setUser(newUser);
    return newUser;
  };

  const logout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('admin_token_backup');
    localStorage.removeItem('impersonating');
    setToken(null);
    setUser(null);
  };

  const impersonate = (accessToken, userData) => {
    const current = localStorage.getItem('token');
    if (current) localStorage.setItem('admin_token_backup', current);
    localStorage.setItem('impersonating', '1');
    localStorage.setItem('token', accessToken);
    setToken(accessToken);
    setUser(userData);
  };

  const exitImpersonation = async () => {
    const backup = localStorage.getItem('admin_token_backup');
    localStorage.removeItem('impersonating');
    localStorage.removeItem('admin_token_backup');
    if (backup) {
      localStorage.setItem('token', backup);
      setToken(backup);
      try {
        const res = await axios.get(`${API_URL}/auth/me`, { headers: { Authorization: `Bearer ${backup}` } });
        setUser(res.data);
        return res.data;
      } catch (e) { /* fall through to logout */ }
    }
    logout();
    return null;
  };

  const isImpersonating = () => !!localStorage.getItem('impersonating');

  const getAuthHeader = () => ({
    headers: { Authorization: `Bearer ${token}` }
  });

  return (
    <AuthContext.Provider value={{ user, token, isLoading, login, register, logout, getAuthHeader, impersonate, exitImpersonation, isImpersonating }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
